// src/components/gamification/ProfileCard3D.tsx
// TRUE ReactBits-style 3D profile card with tilt effects and glassmorphism
// Based on https://reactbits.dev/components/profile-card

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface ProfileCard3DProps {
	avatarUrl: string;
	name?: string;
	title?: string;
	handle?: string;
	status?: string;
	contactText?: string;
	showUserInfo?: boolean;
	enableTilt?: boolean;
	enableMobileTilt?: boolean;
	mobileTiltSensitivity?: number;
	className?: string;
	platform?: string;
	metrics?: {
		followers: number;
		engagement: number;
		views: number;
	};
	onContactClick?: () => void;
}

// Gradient configurations for different platforms
const PLATFORM_GRADIENTS = {
	twitter: {
		behind: "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), rgba(29, 155, 240, var(--card-opacity)) 4%, rgba(29, 155, 240, calc(var(--card-opacity)*0.75)) 10%, rgba(29, 155, 240, calc(var(--card-opacity)*0.5)) 50%, rgba(29, 155, 240, 0) 100%), radial-gradient(35% 52% at 55% 20%, #1d9bf0c4 0%, #073aff00 100%), radial-gradient(100% 100% at 50% 50%, #1d9bf0ff 1%, #073aff00 76%), conic-gradient(from 124deg at 50% 50%, #1d9bf0ff 0%, #07c6ffff 40%, #07c6ffff 60%, #1d9bf0ff 100%)",
		inner: "linear-gradient(145deg, #1d9bf08c 0%, #71C4FF44 100%)",
	},
	facebook: {
		behind: "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), rgba(24, 119, 242, var(--card-opacity)) 4%, rgba(24, 119, 242, calc(var(--card-opacity)*0.75)) 10%, rgba(24, 119, 242, calc(var(--card-opacity)*0.5)) 50%, rgba(24, 119, 242, 0) 100%), radial-gradient(35% 52% at 55% 20%, #1877f2c4 0%, #073aff00 100%), radial-gradient(100% 100% at 50% 50%, #1877f2ff 1%, #073aff00 76%), conic-gradient(from 124deg at 50% 50%, #1877f2ff 0%, #07c6ffff 40%, #07c6ffff 60%, #1877f2ff 100%)",
		inner: "linear-gradient(145deg, #1877f28c 0%, #71C4FF44 100%)",
	},
	linkedin: {
		behind: "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), rgba(10, 102, 194, var(--card-opacity)) 4%, rgba(10, 102, 194, calc(var(--card-opacity)*0.75)) 10%, rgba(10, 102, 194, calc(var(--card-opacity)*0.5)) 50%, rgba(10, 102, 194, 0) 100%), radial-gradient(35% 52% at 55% 20%, #0a66c2c4 0%, #073aff00 100%), radial-gradient(100% 100% at 50% 50%, #0a66c2ff 1%, #073aff00 76%), conic-gradient(from 124deg at 50% 50%, #0a66c2ff 0%, #07c6ffff 40%, #07c6ffff 60%, #0a66c2ff 100%)",
		inner: "linear-gradient(145deg, #0a66c28c 0%, #71C4FF44 100%)",
	},
	tiktok: {
		behind: "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), rgba(255, 0, 80, var(--card-opacity)) 4%, rgba(255, 0, 80, calc(var(--card-opacity)*0.75)) 10%, rgba(255, 0, 80, calc(var(--card-opacity)*0.5)) 50%, rgba(255, 0, 80, 0) 100%), radial-gradient(35% 52% at 55% 20%, #ff0050c4 0%, #073aff00 100%), radial-gradient(100% 100% at 50% 50%, #ff0050ff 1%, #073aff00 76%), conic-gradient(from 124deg at 50% 50%, #ff0050ff 0%, #07c6ffff 40%, #07c6ffff 60%, #ff0050ff 100%)",
		inner: "linear-gradient(145deg, #ff00508c 0%, #71C4FF44 100%)",
	},
	default: {
		behind: "radial-gradient(farthest-side circle at var(--pointer-x) var(--pointer-y), hsla(266, 100%, 90%, var(--card-opacity)) 4%, hsla(266, 50%, 80%, calc(var(--card-opacity)*0.75)) 10%, hsla(266, 25%, 70%, calc(var(--card-opacity)*0.5)) 50%, hsla(266, 0%, 60%, 0) 100%), radial-gradient(35% 52% at 55% 20%, #00ffaac4 0%, #073aff00 100%), radial-gradient(100% 100% at 50% 50%, #00c1ffff 1%, #073aff00 76%), conic-gradient(from 124deg at 50% 50%, #c137ffff 0%, #07c6ffff 40%, #07c6ffff 60%, #c137ffff 100%)",
		inner: "linear-gradient(145deg, #60496e8c 0%, #71C4FF44 100%)",
	},
};

const ANIMATION_CONFIG = {
	SMOOTH_DURATION: 600,
	INITIAL_DURATION: 1500,
	INITIAL_X_OFFSET: 70,
	INITIAL_Y_OFFSET: 60,
	DEVICE_BETA_OFFSET: 20,
} as const;

const clamp = (value: number, min = 0, max = 100): number =>
	Math.min(Math.max(value, min), max);

const round = (value: number, precision = 3): number =>
	parseFloat(value.toFixed(precision));

const adjust = (
	value: number,
	fromMin: number,
	fromMax: number,
	toMin: number,
	toMax: number
): number =>
	round(toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin));

const easeInOutCubic = (x: number): number =>
	x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

const formatNumber = (num: number): string => {
	if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
	if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
	return num.toString();
};

export function ProfileCard3D({
	avatarUrl,
	name = "Social Profile",
	title = "Content Creator",
	handle = "user",
	status = "Active",
	contactText = "View Stats",
	showUserInfo = true,
	enableTilt = true,
	enableMobileTilt = false,
	mobileTiltSensitivity = 5,
	className = "",
	platform = "default",
	metrics,
	onContactClick,
}: ProfileCard3DProps) {
	const wrapRef = useRef<HTMLDivElement>(null);
	const cardRef = useRef<HTMLDivElement>(null);

	const gradients = PLATFORM_GRADIENTS[platform as keyof typeof PLATFORM_GRADIENTS] || PLATFORM_GRADIENTS.default;

	const animationHandlers = useMemo(() => {
		if (!enableTilt) return null;

		let rafId: number | null = null;

		const updateCardTransform = (
			offsetX: number,
			offsetY: number,
			card: HTMLElement,
			wrap: HTMLElement
		) => {
			const width = card.clientWidth;
			const height = card.clientHeight;

			const percentX = clamp((100 / width) * offsetX);
			const percentY = clamp((100 / height) * offsetY);

			const centerX = percentX - 50;
			const centerY = percentY - 50;

			const properties = {
				"--pointer-x": `${percentX}%`,
				"--pointer-y": `${percentY}%`,
				"--background-x": `${adjust(percentX, 0, 100, 35, 65)}%`,
				"--background-y": `${adjust(percentY, 0, 100, 35, 65)}%`,
				"--pointer-from-center": `${clamp(Math.hypot(percentY - 50, percentX - 50) / 50, 0, 1)}`,
				"--pointer-from-top": `${percentY / 100}`,
				"--pointer-from-left": `${percentX / 100}`,
				"--rotate-x": `${round(-(centerX / 5))}deg`,
				"--rotate-y": `${round(centerY / 4)}deg`,
			};

			Object.entries(properties).forEach(([property, value]) => {
				wrap.style.setProperty(property, value);
			});
		};

		const createSmoothAnimation = (
			duration: number,
			startX: number,
			startY: number,
			card: HTMLElement,
			wrap: HTMLElement
		) => {
			const startTime = performance.now();
			const targetX = wrap.clientWidth / 2;
			const targetY = wrap.clientHeight / 2;

			const animationLoop = (currentTime: number) => {
				const elapsed = currentTime - startTime;
				const progress = clamp(elapsed / duration);
				const easedProgress = easeInOutCubic(progress);

				const currentX = adjust(easedProgress, 0, 1, startX, targetX);
				const currentY = adjust(easedProgress, 0, 1, startY, targetY);

				updateCardTransform(currentX, currentY, card, wrap);

				if (progress < 1) {
					rafId = requestAnimationFrame(animationLoop);
				}
			};

			rafId = requestAnimationFrame(animationLoop);
		};

		return {
			updateCardTransform,
			createSmoothAnimation,
			cancelAnimation: () => {
				if (rafId) {
					cancelAnimationFrame(rafId);
					rafId = null;
				}
			},
		};
	}, [enableTilt]);

	const handlePointerMove = useCallback(
		(event: PointerEvent) => {
			const card = cardRef.current;
			const wrap = wrapRef.current;

			if (!card || !wrap || !animationHandlers) return;

			const rect = card.getBoundingClientRect();
			animationHandlers.updateCardTransform(
				event.clientX - rect.left,
				event.clientY - rect.top,
				card,
				wrap
			);
		},
		[animationHandlers]
	);

	const handlePointerEnter = useCallback(() => {
		const card = cardRef.current;
		const wrap = wrapRef.current;

		if (!card || !wrap || !animationHandlers) return;

		animationHandlers.cancelAnimation();
		wrap.classList.add('pc-active');
		card.classList.add('pc-active');
	}, [animationHandlers]);

	const handlePointerLeave = useCallback(
		(event: PointerEvent) => {
			const card = cardRef.current;
			const wrap = wrapRef.current;

			if (!card || !wrap || !animationHandlers) return;

			animationHandlers.createSmoothAnimation(
				ANIMATION_CONFIG.SMOOTH_DURATION,
				event.offsetX,
				event.offsetY,
				card,
				wrap
			);
			wrap.classList.remove('pc-active');
			card.classList.remove('pc-active');
		},
		[animationHandlers]
	);

	useEffect(() => {
		if (!enableTilt || !animationHandlers) return;

		const card = cardRef.current;
		const wrap = wrapRef.current;

		if (!card || !wrap) return;

		const pointerMoveHandler = handlePointerMove as EventListener;
		const pointerEnterHandler = handlePointerEnter as EventListener;
		const pointerLeaveHandler = handlePointerLeave as EventListener;

		card.addEventListener("pointerenter", pointerEnterHandler);
		card.addEventListener("pointermove", pointerMoveHandler);
		card.addEventListener("pointerleave", pointerLeaveHandler);

		const initialX = wrap.clientWidth - ANIMATION_CONFIG.INITIAL_X_OFFSET;
		const initialY = ANIMATION_CONFIG.INITIAL_Y_OFFSET;

		animationHandlers.updateCardTransform(initialX, initialY, card, wrap);
		animationHandlers.createSmoothAnimation(
			ANIMATION_CONFIG.INITIAL_DURATION,
			initialX,
			initialY,
			card,
			wrap
		);

		return () => {
			card.removeEventListener("pointerenter", pointerEnterHandler);
			card.removeEventListener("pointermove", pointerMoveHandler);
			card.removeEventListener("pointerleave", pointerLeaveHandler);
			animationHandlers.cancelAnimation();
		};
	}, [
		enableTilt,
		animationHandlers,
		handlePointerMove,
		handlePointerEnter,
		handlePointerLeave,
	]);

	const cardStyle = useMemo(
		() =>
			({
				"--behind-gradient": gradients.behind,
				"--inner-gradient": gradients.inner,
				"--card-opacity": 0.7,
			}) as React.CSSProperties,
		[gradients]
	);

	return (
		<div
			ref={wrapRef}
			className={cn("pc-card-wrapper", className)}
			style={cardStyle}
		>
			<section ref={cardRef} className="pc-card">
				<div className="pc-inside">
					<div className="pc-shine" />
					<div className="pc-glare" />
					<div className="pc-content pc-avatar-content">
						<img
							className="pc-avatar"
							src={avatarUrl}
							alt={`${name} avatar`}
							loading="lazy"
							onError={(e) => {
								const target = e.target as HTMLImageElement;
								target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`;
							}}
						/>
						{showUserInfo && (
							<div className="pc-user-info">
								<div className="pc-user-details">
									<div className="pc-mini-avatar">
										<img
											src={avatarUrl}
											alt={`${name} mini avatar`}
											loading="lazy"
											onError={(e) => {
												const target = e.target as HTMLImageElement;
												target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${name}`;
											}}
										/>
									</div>
									<div className="pc-user-text">
										<div className="pc-handle">@{handle}</div>
										<div className="pc-status">{status}</div>
									</div>
								</div>
								{metrics && (
									<div className="pc-metrics">
										<div className="pc-metric">
											<span className="pc-metric-value">{formatNumber(metrics.followers)}</span>
											<span className="pc-metric-label">Followers</span>
										</div>
										<div className="pc-metric">
											<span className="pc-metric-value">{formatNumber(metrics.engagement)}</span>
											<span className="pc-metric-label">Engagement</span>
										</div>
										<div className="pc-metric">
											<span className="pc-metric-value">{formatNumber(metrics.views)}</span>
											<span className="pc-metric-label">Views</span>
										</div>
									</div>
								)}
								<button
									className="pc-contact-btn"
									onClick={onContactClick}
									style={{ pointerEvents: "auto" }}
									type="button"
									aria-label={`View ${name} stats`}
								>
									{contactText}
								</button>
							</div>
						)}
					</div>
					<div className="pc-content">
						<div className="pc-details">
							<h3>{name}</h3>
							<p>{title}</p>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}